import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import de from '../i18n/de.json';
import en from '../i18n/en.json';
import type { SupportedLocale } from '../i18n/resolveLocale';
import { resolveLocale } from '../i18n/resolveLocale';

type Dict = typeof en;

const DICTS: Record<SupportedLocale, Dict> = { de, en };

type Ctx = {
  locale: SupportedLocale;
  setLocale: (l: SupportedLocale) => void;
  t: (path: string) => string;
};

const I18nContext = createContext<Ctx | null>(null);

export function I18nProvider({
  initialLocale,
  children,
}: {
  initialLocale: string | null | undefined;
  children: ReactNode;
}) {
  const [locale, setLocale] = useState<SupportedLocale>(() => resolveLocale(initialLocale));

  const t = useCallback(
    (path: string): string => {
      const parts = path.split('.');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let node: any = DICTS[locale];
      for (const p of parts) {
        if (node && typeof node === 'object' && p in node) {
          node = node[p];
        } else {
          return path; // surface missing-key as the key itself — easier to spot in tests
        }
      }
      return typeof node === 'string' ? node : path;
    },
    [locale],
  );

  const value = useMemo<Ctx>(() => ({ locale, setLocale, t }), [locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useT(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used inside I18nProvider');
  return ctx;
}
